import React, { useState, useEffect } from 'react';
import styles from './LayoutEdit.module.css';
import { useNavigate } from 'react-router-dom';

const LayoutEdit = ({ layout, isOpen, onClose, onSave }) => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        type: 'indoor',
        description: '',
        size: {
            width: 0,
            height: 0
        },
        mapImage: '',
        imageFile: null
    });
    const [imagePreview, setImagePreview] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (layout) {
            setFormData({
                name: layout.name || '',
                type: layout.type || 'room',
                description: layout.description || '',
                size: layout.size || { width: 800, height: 600 },
                mapImage: `${process.env.REACT_APP_API_BASE_URL}${layout.mapImage}` || ''
            });
            setImagePreview(`${process.env.REACT_APP_API_BASE_URL}${layout.mapImage}` || null);
        }
    }, [layout]);

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }
        if (!formData.imageFile && (!layout || !layout.mapImage)) {
            newErrors.imageUrl = 'Please upload an image file';
        }
        if (!formData.size.width || formData.size.width < 100) {
            newErrors.width = 'Width must be at least 100px';
        }
        if (!formData.size.height || formData.size.height < 100) {
            newErrors.height = 'Height must be at least 100px';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsLoading(true);
        try {
            // Create FormData object
            const submitData = new FormData();
            
            // Add all form fields
            submitData.append('name', formData.name);
            submitData.append('type', formData.type);
            submitData.append('description', formData.description);
            submitData.append('size[width]', formData.size.width);
            submitData.append('size[height]', formData.size.height);

            // Add image file if exists
            if (formData.imageFile) {
                submitData.append('image', formData.imageFile);
            }

            // If editing and no new file is selected, keep existing mapImage
            if (layout && !formData.imageFile) {
                submitData.append('mapImage', layout.mapImage || '');
            }
            console.log('Submitting data:', formData);

            // Save the layout
            const result = await onSave(submitData);
            onClose();
            // Directly to Layout Details if new layout is created
            if(!layout?._id)
                navigate(`/layout/${result._id}`);
        } catch (error) {
            console.error('Error saving layout:', error);
            setErrors({ submit: 'Failed to save layout. Please try again.' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file && file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = (event) => {
                const dataUrl = event.target.result;
                setImagePreview(dataUrl);
                
                // Load image to get dimensions
                const img = new Image();
                img.onload = () => {
                    setFormData(prev => ({
                        ...prev,
                        imageFile: file,
                        mapImage: '',
                        size: {
                            width: img.naturalWidth,
                            height: img.naturalHeight
                        }
                    }));
                };
                img.src = dataUrl;
            };
            reader.readAsDataURL(file);
        }
        else{
            console.error('Selected file is not a valid image');
        }
    };

    if (!isOpen) return null;

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <h2>{layout ? 'Edit Layout' : 'Create New Layout'}</h2>
                    <button className={styles.closeButton} onClick={onClose}>×</button>
                </div>

                <form onSubmit={handleSubmit} className={styles.form}>
                    <div className={styles.formGrid}>
                        {/* Left Column - Form Fields */}
                        <div className={styles.formFields}>
                            <div className={styles.formGroup}>
                                <label htmlFor="name">Layout Name *</label>
                                <input
                                    type="text"
                                    id="name"
                                    value={formData.name}
                                    onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="Enter layout name"
                                    className={errors.name ? styles.error : ''}
                                />
                                {errors.name && <span className={styles.errorText}>{errors.name}</span>}
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="type">Layout Type</label>
                                <select
                                    id="type"
                                    value={formData.type}
                                    onChange={e => setFormData(prev => ({ ...prev, type: e.target.value }))}
                                >
                                    <option value="indoor">Indoor</option>
                                    <option value="outdoor">Outdoor</option>
                                </select>
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="description">Description</label>
                                <textarea
                                    id="description"
                                    value={formData.description}
                                    onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                    placeholder="Enter layout description"
                                    rows={3}
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label>Layout Image *</label>
                                <div className={styles.imageInputs}>
                                    <div className={styles.fileInputWrapper}>
                                        <input
                                            type="file"
                                            id="imageFile"
                                            accept="image/*"
                                            onChange={handleFileSelect}
                                            className={styles.fileInput}
                                        />
                                        <label htmlFor="imageFile" className={styles.fileInputLabel}>
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                                <polyline points="17 8 12 3 7 8"/>
                                                <line x1="12" y1="3" x2="12" y2="15"/>
                                            </svg>
                                            Choose File
                                        </label>
                                    </div>
                                </div>
                                {errors.imageUrl && <span className={styles.errorText}>{errors.imageUrl}</span>}
                            </div>

                            <div className={styles.sizeInputs}>
                                <div className={styles.formGroup}>
                                    <label htmlFor="width">Width (px)</label>
                                    <input
                                        type="number"
                                        id="width"
                                        value={formData.size.width}
                                        readOnly
                                        disabled
                                        className={styles.disabledInput}
                                    />
                                </div>

                                <div className={styles.formGroup}>
                                    <label htmlFor="height">Height (px)</label>
                                    <input
                                        type="number"
                                        id="height"
                                        value={formData.size.height}
                                        readOnly
                                        disabled
                                        className={styles.disabledInput}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Right Column - Image Preview */}
                        <div className={styles.imagePreview}>
                            {imagePreview ? (
                                <img
                                    src={imagePreview}
                                    alt="Layout preview"
                                    onError={() => setImagePreview(null)}
                                />
                            ) : (
                                <div className={styles.placeholderImage}>
                                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                        <circle cx="8.5" cy="8.5" r="1.5"/>
                                        <polyline points="21 15 16 10 5 21"/>
                                    </svg>
                                    <p>Image Preview</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {errors.submit && (
                        <div className={styles.submitError}>
                            {errors.submit}
                        </div>
                    )}

                    <div className={styles.formActions}>
                        <button
                            type="button"
                            onClick={onClose}
                            className={styles.cancelButton}
                            disabled={isLoading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className={styles.saveButton}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Saving...' : 'Save Layout'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default LayoutEdit;
